// ============================================================
// useFeeTypes Hooks
// ============================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSchool } from './useSchool';
import { feeTypeService } from '@/services/feeTypeService';

/**
 * Hook to fetch all fee types for current school
 */
export function useFeeTypes(skip = 0, limit = 100) {
  const { schoolId } = useSchool();

  return useQuery({
    queryKey: ['feeTypes', schoolId, skip, limit],
    queryFn: () => feeTypeService.getFeeTypes(schoolId, skip, limit),
    enabled: !!schoolId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch single fee type by ID
 */
export function useFeeType(id) {
  const { schoolId } = useSchool();

  return useQuery({
    queryKey: ['feeType', schoolId, id],
    queryFn: () => feeTypeService.getFeeTypeById(schoolId, id),
    enabled: !!schoolId && !!id,
  });
}

/**
 * Hook to create a new fee type
 */
export function useCreateFeeType() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => feeTypeService.createFeeType(schoolId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feeTypes', schoolId] });
    },
  });
}

/**
 * Hook to update an existing fee type
 */
export function useUpdateFeeType() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }) => feeTypeService.updateFeeType(schoolId, id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['feeTypes', schoolId] });
      queryClient.invalidateQueries({ queryKey: ['feeType', schoolId, variables.id] });
    },
  });
}

/**
 * Hook to delete a fee type
 */
export function useDeleteFeeType() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => feeTypeService.deleteFeeType(schoolId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feeTypes', schoolId] });
    },
  });
}
